import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CACHE_KEY = 'site_settings_cache';

// localStorage'dan anlık yükleme (flash önleme)
const getCachedSettings = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) return JSON.parse(cached);
  } catch (err) { console.error('Cache parse failed:', err); }
  return null;
};

const defaultSettings = {
  siteName: '',
  siteDescription: '',
  logoUrl: '',
  logoDarkUrl: '',
  faviconUrl: '/favicon.ico',
  pendingRequestsSliderEnabled: true,
  buildYourOwnProviderEnabled: true,
  loading: true,
};

const SiteSettingsContext = createContext(defaultSettings);

export const SiteSettingsProvider = ({ children }) => {
  const cached = getCachedSettings();
  const [settings, setSettings] = useState(cached || defaultSettings);

  // İlk render'dan ago window.__SITE_NAME__ set edilsin (SSR/SEO yardımcıları için)
  if (typeof window !== 'undefined' && cached?.siteName && !window.__SITE_NAME__) {
    window.__SITE_NAME__ = cached.siteName;
  }

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/public/site-settings`);
        if (res.data) {
          const newSettings = {
            siteName: res.data.site_name || '',
            siteDescription: res.data.site_description || '',
            logoUrl: res.data.logo_url || '',
            logoDarkUrl: res.data.logo_dark_url || '',
            faviconUrl: res.data.favicon_url || '/favicon.ico',
            // Iter401-P10: pending-requests slider toggle (default on)
            pendingRequestsSliderEnabled: res.data.pending_requests_slider_enabled !== false,
            // Iter525: Build-Your-Own provider package toggle (default on)
            buildYourOwnProviderEnabled: res.data.build_your_own_provider_enabled !== false,
            loading: false,
          };
          setSettings(newSettings);
          // SEO utility'lerinin runtime erişebilmesi for global'e yaz
          if (typeof window !== 'undefined') {
            window.__SITE_NAME__ = newSettings.siteName || '';
          }
          // Sadece değişiklik varsa cache güncelle
          const oldCache = localStorage.getItem(CACHE_KEY);
          if (JSON.stringify(newSettings) !== oldCache) {
            localStorage.setItem(CACHE_KEY, JSON.stringify(newSettings));
          }
          if (res.data.site_name) {
            // Title artık SEOHead bileşeni tarafından yönetiliyor
            // Sadece SEOHead olmayan sayfalarda fallback olarak kullan
            if (!document.querySelector('meta[property="og:title"]')?.content) {
              document.title = res.data.site_name;
            }
          }
          // Iter383: Dinamik meta açıklama + og tag güncelleme (statik index.html fallback'i temizle)
          const upsertMeta = (selector, attr, content) => {
            if (!content) return;
            let el = document.querySelector(selector);
            if (!el) {
              el = document.createElement('meta');
              const [, key, val] = selector.match(/\[(\w+)="([^"]+)"\]/) || [];
              if (key && val) el.setAttribute(key, val);
              document.head.appendChild(el);
            }
            el.setAttribute(attr, content);
          };
          const siteName = res.data.site_name || '';
          const siteDesc = res.data.site_description || '';
          if (siteName) {
            upsertMeta('meta[property="og:site_name"]', 'content', siteName);
            upsertMeta('meta[name="apple-mobile-web-app-title"]', 'content', siteName);
          }
          if (siteDesc) {
            upsertMeta('meta[name="description"]', 'content', siteDesc);
            upsertMeta('meta[property="og:description"]', 'content', siteDesc);
          }
          // Dinamik favicon güncelleme
          if (res.data.favicon_url) {
            const existingFavicon = document.querySelector('link[rel="icon"]');
            const faviconUrl = res.data.favicon_url + (res.data.favicon_url.includes('?') ? '&' : '?') + 'v=' + Date.now();
            if (existingFavicon) {
              existingFavicon.href = faviconUrl;
            } else {
              const link = document.createElement('link');
              link.rel = 'icon';
              link.href = faviconUrl;
              document.head.appendChild(link);
            }
          }
          // Dinamik apple-touch-icon güncelleme
          if (res.data.logo_url) {
            const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
            const logoUrl = res.data.logo_url + (res.data.logo_url.includes('?') ? '&' : '?') + 'v=' + Date.now();
            if (appleTouchIcon) {
              appleTouchIcon.href = logoUrl;
            }
          }
        }
      } catch (e) { console.error(e);
        setSettings(prev => ({ ...prev, loading: false }));
      }
    };
    fetchSettings();
  }, []);

  return (
    <SiteSettingsContext.Provider value={settings}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => useContext(SiteSettingsContext);
export const useSiteName = () => useContext(SiteSettingsContext).siteName;
export const useSiteLogo = () => {
  const ctx = useContext(SiteSettingsContext);
  return { logoUrl: ctx.logoUrl, logoDarkUrl: ctx.logoDarkUrl };
};
